import { logger } from 'src/utils/logger';
import { useState, useEffect } from 'react';
import { WorkflowDefinition } from 'src/@types/selfServeTypes/workflowDefinition.interface';
import { checkWorkflowPermissions } from '../commonComponents/WorkflowTable/workflowPermissionUtils';

export function useExecuteWorkflowPermissions(
	allWorkflows: WorkflowDefinition[],
	userName: string | undefined
) {
	const [executableWorkflows, setExecutableWorkflows] = useState<WorkflowDefinition[]>([]);
	const [executeWorkflowIds, setExecuteWorkflowIds] = useState<Set<string>>(new Set());
	const [isCheckingPermissions, setIsCheckingPermissions] = useState(false);
	const [permissionError, setPermissionError] = useState<string | null>(null);

	useEffect(() => {
		async function checkPermissions() {
			if (userName && allWorkflows.length > 0) {
				setIsCheckingPermissions(true);
				setPermissionError(null);

				try {
					const activeWorkflows = allWorkflows.filter((w) => w.status === 'ACTIVE');
					const workflowIds = activeWorkflows.map((w) => w.workflowId);

					// Check both execute and submit permissions in parallel
					const [execAuthorizedIds, submitAuthorizedIds] = await Promise.all([
						checkWorkflowPermissions(workflowIds, userName, 'execute'),
						checkWorkflowPermissions(workflowIds, userName, 'submit'),
					]);

					// executeWorkflowIds tracks execute-only so global alert blocking
					// can distinguish execute vs submit-only workflows.
					const execSet = new Set(execAuthorizedIds);
					const allAuthorizedIds = new Set([...execAuthorizedIds, ...submitAuthorizedIds]);

					const filtered = activeWorkflows.filter((workflow) =>
						allAuthorizedIds.has(workflow.workflowId)
					);

					setExecutableWorkflows(filtered);
					setExecuteWorkflowIds(execSet);
				} catch (error) {
					logger.error(`Failed to check workflow permissions [ userName ] ::: [ ${userName} ] [ error ] ::: [ ${JSON.stringify(error)} ]`);
					setPermissionError('Failed to check workflow permissions. Please reload the page to try again.');
					setExecutableWorkflows([]);
					setExecuteWorkflowIds(new Set());
				} finally {
					setIsCheckingPermissions(false);
				}
			}
		}

		checkPermissions();
	}, [userName, allWorkflows]);

	return { executableWorkflows, executeWorkflowIds, isCheckingPermissions, permissionError };
}
